import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DataGridColumnAlignments, DataGridColumnWidthTypes} from "../../../../../containers/data-grid";
import {Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import Paper from "@mui/material/Paper";
import PartnershipViewSellers from "./sellers";
import PartnershipViewBuyers from "./buyers";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const dataGridColumns = {
    buyers: 'buyers',
    sellers: 'sellers',
    publicProducts: 'publicProducts',
}

const tabs = {
    buyers: 'buyers',
    sellers: 'sellers'
}

const PartnershipView = () => {
    const [tab, setTab] = useState(tabs.buyers);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [summary, setSummary] = useState({});
    const isMounted = useIsMounted();

    const {configureNotebookTags} = useConfigureNotebook('partnership-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.partnership);
        configureBehavioralTool((tags) => tags.partnership);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the summary info for the partnerships
     * @type {(function(): Promise<void>)}
     */
    const getPartnershipSummaryInfo = useCallback(async () => {
        // TODO: call api to get the summary info
        setLoadingSummary(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        const response = {
            resultFlag: true,
            data: {
                buyers: 100,
                sellers: 50,
                publicProducts: 500,
            }
        }
        if (!isMounted())
            return;
        setLoadingSummary(false);
        if (response?.resultFlag) {
            setSummary(response.data);
        }
    }, [isMounted])

    /**
     * Fetches the data for the partnerships
     */
    useEffect(() => {
        void getPartnershipSummaryInfo();
    }, [getPartnershipSummaryInfo])

    const summaryColumns = useMemo(() =>
        /**@type {DataGridColumn[]} */
        [
            {
                title: 'Buyers',
                name: 'buyers',
                width: {type: DataGridColumnWidthTypes.flex, size: 1},
                alignment: DataGridColumnAlignments.center,
                savable: false,
                reorderable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                resizable: false,
                sortable: false,
            },
            {
                title: 'Sellers',
                name: 'sellers',
                width: {type: DataGridColumnWidthTypes.flex, size: 1},
                alignment: DataGridColumnAlignments.center,
                savable: false,
                reorderable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                resizable: false,
                sortable: false,
            },
            {
                title: 'Public Products',
                name: 'publicProducts',
                width: {type: DataGridColumnWidthTypes.flex, size: 2},
                alignment: DataGridColumnAlignments.center,
                savable: false,
                reorderable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                resizable: false,
                sortable: false,
            }
        ], [])

    const summaryRows = useMemo(() => {
        return [{
            key: 'value',
            data: null,
            cells: {
                [dataGridColumns.buyers]:
                    loadingSummary
                        ? <CircularProgress size={20}/>
                        : `${summary.buyers ?? 0} `,
                [dataGridColumns.sellers]:
                    loadingSummary
                        ? <CircularProgress size={20}/>
                        : `${summary.sellers ?? 0} `,
                [dataGridColumns.publicProducts]:
                    loadingSummary
                        ? <CircularProgress size={20}/>
                        : `${summary.publicProducts ?? 0} `,
            },
        }]
    }, [loadingSummary, summary?.buyers, summary?.publicProducts, summary?.sellers])

    /**
     * Changes the tab
     * @type {(function(*, *): void)}
     */
    const onTabChange = useCallback((_, newValue) => {
        if (!newValue)
            return;
        setTab(newValue);
    }, [])


    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback((theme) => {
        return ({
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            width: '100%',
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.palette.text.primary,
                height: 30,
                marginBlock: 0.5,
                marginInline: 0.5,
                padding: 1,
                width: '50%',
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
            },
        })
    }, []);

    return (
        <>
            <Box sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBlockEnd: 2
            }}>
                <Typography variant={'h6'} fontWeight={600} sx={{marginBlockEnd: 4}}>
                    Partnerships
                </Typography>
                {/*<PartnerDataGrid*/}
                {/*    classNames={{*/}
                {/*        container: 'w-100 w-md-50 w-xl-35 mb-3',*/}
                {/*        table: 'w-100',*/}
                {/*        layout: 'mt-2'*/}
                {/*    }}*/}
                {/*    rows={summaryRows}*/}
                {/*    columns={summaryColumns}*/}
                {/*    hideFooter*/}
                {/*    hideToolbar*/}
                {/*/>*/}
            </Box>
            <Paper sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                padding: 2,
            }}>
                <ToggleButtonGroup
                    orientation="horizontal"
                    value={tab}
                    exclusive
                    onChange={(e, value) => onTabChange(e, value)}
                    sx={toggleButtonGroupMixin}
                >
                    <ToggleButton value={tabs.buyers}>
                        <Typography variant={'overline'} fontWeight={600}>
                            Buyers
                        </Typography>
                    </ToggleButton>
                    <ToggleButton value={tabs.sellers}>
                        <Typography variant={'overline'} fontWeight={600}>
                            Sellers
                        </Typography>
                    </ToggleButton>
                </ToggleButtonGroup>
                {
                    tab === tabs.buyers &&
                    <PartnershipViewBuyers/>
                }
                {
                    tab === tabs.sellers &&
                    <PartnershipViewSellers/>
                }
            </Paper>
        </>
    );
}


export default PartnershipView;
